/**
 * useFieldsConfig Hook
 * Manages field configuration fetching
 */

import { useState, useEffect } from "react";
import {
  getRegulationsFields,
  type FieldConfig,
} from "../services/regulationsService";

interface UseFieldsConfigResult {
  fields: FieldConfig[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook for fetching field configuration
 * @param pageType - Page type ('list' or 'detail')
 * @returns Object containing fields, loading state, and error
 */
export function useFieldsConfig(pageType: string): UseFieldsConfigResult {
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getRegulationsFields(pageType);

        if (result.status === "success" && result.data) {
          setFields(result.data.fields);
        } else {
          setError(result.message || "Failed to load field configuration");
          setFields([]);
        }
      } catch (err) {
        console.error("[useFieldsConfig] Error fetching fields:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        setFields([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [pageType]);

  return {
    fields,
    loading,
    error,
  };
}
